import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/**
 * CountUp — RAF-based number counter. Triggers when the element scrolls into view.
 */
export function CountUp({ value = 0, duration = 1.4, format, prefix = "", suffix = "", className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!inView) return;
    const target = Number(value) || 0;
    const durMs = duration * 1000;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const tick = (ts) => {
      if (startRef.current == null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const p = Math.min(1, elapsed / durMs);
      setDisplay(target * easeOut(p));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      startRef.current = null;
    };
  }, [inView, value, duration]);

  const rounded = Math.round(display);
  const formatted = new Intl.NumberFormat("en-IN").format(rounded);
  return <span ref={ref} className={className}>{prefix}{formatted}{suffix}</span>;
}

export function Reveal({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
